import React, { useState, useRef, useEffect, useMemo } from 'react';
import Map, { Marker, Source, Layer, ScaleControl } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { GeoPoint } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { useMapboxToken } from './MapboxTokenContext';


const lineLayerStyle = {
  id: 'route-line',
  type: 'line',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: { 'line-color': '#007bff', 'line-width': 4 },
};

const MATCH_THRESHOLD_METERS = 100;

const calculateRouteDistanceMeters = (points) => {
  const earthRadiusMeters = 6371000;
  let distance = 0;

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const latitudeDelta = (current.lat - previous.lat) * Math.PI / 180;
    const longitudeDelta = (current.lng - previous.lng) * Math.PI / 180;
    const latitude1 = previous.lat * Math.PI / 180;
    const latitude2 = current.lat * Math.PI / 180;
    const haversine = Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(latitude1) * Math.cos(latitude2) * Math.sin(longitudeDelta / 2) ** 2;
    distance += 2 * earthRadiusMeters * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  return distance;
};

const matchingThresholdFeature = (point) => {
  if (!point) return { type: 'FeatureCollection', features: [] };
  const earthRadiusMeters = 6371000;
  const angularDistance = MATCH_THRESHOLD_METERS / earthRadiusMeters;
  const latitude = point.lat * Math.PI / 180;
  const longitude = point.lng * Math.PI / 180;
  const coordinates = Array.from({ length: 65 }, (_, index) => {
    const bearing = index * 2 * Math.PI / 64;
    const ringLatitude = Math.asin(
      Math.sin(latitude) * Math.cos(angularDistance) +
      Math.cos(latitude) * Math.sin(angularDistance) * Math.cos(bearing)
    );
    const ringLongitude = longitude + Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitude),
      Math.cos(angularDistance) - Math.sin(latitude) * Math.sin(ringLatitude)
    );
    return [ringLongitude * 180 / Math.PI, ringLatitude * 180 / Math.PI];
  });

  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [coordinates] }
    }]
  };
};

const SwimRouteEditor = ({ initialRoute, onSave, onCancel, seriesId = null, swimId = null }) => {
  const mapboxToken = useMapboxToken();
  // Local state for the route points. 
  // We work with simple objects { lat, lng } internally for easier manipulation
  const [points, setPoints] = useState([]);
  const [selectedPointIndex, setSelectedPointIndex] = useState(null);
  const [editAction, setEditAction] = useState(null);
  const [tempCoords, setTempCoords] = useState({ lat: '', lng: '' });
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/satellite-streets-v12');
  const [prelimMatches, setprelimMatches] = useState([]); // [{ id, userId, routeMatch, coords }]
  const [loadingPrelimMatches, setLoadingPrelimMatches] = useState(false);

  const mapRef = useRef();
  // Initialize points from props
  useEffect(() => {
    if (initialRoute && initialRoute.length > 0) {
      const initialPoints = initialRoute.map(p => ({
        lat: p.latitude,
        lng: p.longitude
      }));
      setPoints(initialPoints);
      setSelectedPointIndex(null);
      setEditAction(null);
    } else {
      setPoints([]);
      setSelectedPointIndex(null);
      setEditAction(null);
    }
  }, [initialRoute]);

  useEffect(() => {
    const selectedPoint = points[selectedPointIndex];
    setTempCoords(selectedPoint
      ? { lat: selectedPoint.lat.toString(), lng: selectedPoint.lng.toString() }
      : { lat: '', lng: '' });
  }, [points, selectedPointIndex]);

  // Calculate initial map bounds
  const initialViewState = useMemo(() => {
    if (initialRoute && initialRoute.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      initialRoute.forEach(p => bounds.extend([p.longitude, p.latitude]));
      return {
        bounds: bounds,
        fitBoundsOptions: { padding: 50 }
      };
    }
    return {
      longitude: -98.5795,
      latitude: 39.8283,
      zoom: 3
    };
  }, [initialRoute]);

  const handleMapClick = (e) => {
    if (editAction === 'add' || editAction === 'insert-before') {
      const newPoint = { lat: e.lngLat.lat, lng: e.lngLat.lng };
      const newPoints = [...points];

      const insertIndex = editAction === 'insert-before' ? selectedPointIndex : newPoints.length;
      newPoints.splice(insertIndex, 0, newPoint);
      setPoints(newPoints);
      setSelectedPointIndex(insertIndex);
      setEditAction(null);
    }
  };

  const handleDeleteSelectedPoint = () => {
    if (selectedPointIndex === null) return;
    const newPoints = points.filter((_, index) => index !== selectedPointIndex);
    setPoints(newPoints);
    setSelectedPointIndex(newPoints.length === 0 ? null : Math.min(selectedPointIndex, newPoints.length - 1));
    setEditAction(null);
  };

  const movePointByDrag = (index, event) => {
    const newPoints = [...points];
    newPoints[index] = { lat: event.lngLat.lat, lng: event.lngLat.lng };
    setPoints(newPoints);
    setSelectedPointIndex(index);
    setEditAction(null);
  };

  const handleSelectedCoordsSave = () => {
    if (selectedPointIndex === null) return;
    const newLat = parseFloat(tempCoords.lat);
    const newLng = parseFloat(tempCoords.lng);

    if (isNaN(newLat) || isNaN(newLng) || newLat < -90 || newLat > 90 || newLng < -180 || newLng > 180) {
      alert("Invalid coordinate values. Latitude must be between -90 and 90. Longitude must be between -180 and 180.");
      // Do not close the input so the user can correct it.
      return;
    }

    const newPoints = [...points];
    newPoints[selectedPointIndex] = { lat: newLat, lng: newLng };
    setPoints(newPoints);
  };

  const handleSave = () => {
    // Convert back to Firestore GeoPoints
    const geoPoints = points.map(p => new GeoPoint(p.lat, p.lng));
    onSave(geoPoints);
  };

  const handleLoadPrelimMatches = async () => {
    if (!seriesId || !swimId) return;
    setLoadingPrelimMatches(true);
    setprelimMatches([]);
    try {
      const matchOfficialSwimFn = httpsCallable(functions, 'matchOfficialSwim');
      const result = await matchOfficialSwimFn({ seriesId, swimId });
      const rawMatches = result.data.matches;

      // Use routePoints returned by the cloud function directly
      // (avoids client-side reads that would be blocked by security rules)
      const withCoords = rawMatches.map(m => ({
        ...m,
        coords: (m.routePoints && m.routePoints.length >= 2)
          ? m.routePoints.map(p => [p.lng, p.lat])
          : []
      }));

      setprelimMatches(withCoords);
    } catch (error) {
      console.error('Error loading preliminary matches:', error);
      alert('Error loading matches: ' + error.message);
    }
    setLoadingPrelimMatches(false);
  };

  // Prepare GeoJSON for the line
  const routeGeoJSON = useMemo(() => {
    return {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: points.map(p => [p.lng, p.lat])
      }
    };
  }, [points]);
  const selectedPoint = selectedPointIndex === null ? null : points[selectedPointIndex];
  const routeDistanceInMeters = useMemo(() => calculateRouteDistanceMeters(points), [points]);
  const previousLegDistanceInMeters = useMemo(() => (
    selectedPointIndex && selectedPointIndex > 0
      ? calculateRouteDistanceMeters(points.slice(selectedPointIndex - 1, selectedPointIndex + 1))
      : null
  ), [points, selectedPointIndex]);
  const nextLegDistanceInMeters = useMemo(() => (
    selectedPointIndex !== null && selectedPointIndex < points.length - 1
      ? calculateRouteDistanceMeters(points.slice(selectedPointIndex, selectedPointIndex + 2))
      : null
  ), [points, selectedPointIndex]);
  const thresholdGeoJSON = useMemo(() => matchingThresholdFeature(selectedPoint), [selectedPoint]);

  return (
    <div style={{ display: 'flex', height: '80vh', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ width: '300px', padding: '20px', borderRight: '1px solid var(--border-color)', overflowY: 'auto', backgroundColor: 'var(--surface-color)', color: 'var(--text-color)' }}>
        <p style={{ fontSize: '0.9rem', color: 'var(--secondary-text-color)' }}>
          {editAction === 'add'
            ? 'Click the map to add a point.'
            : editAction === 'insert-before'
              ? 'Click the map to insert a point before the selected point.'
              : 'Select a route point to edit its coordinates or drag it on the map.'}
        </p>
        <p><strong>{points.length} points</strong></p>
        <p><strong>Distance: {Math.round(routeDistanceInMeters)} m</strong></p>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button
            onClick={() => setEditAction(editAction === 'add' ? null : 'add')}
            title={editAction === 'add' ? 'Stop adding points' : 'Add points'}
            aria-label={editAction === 'add' ? 'Stop adding points' : 'Add points'}
            aria-pressed={editAction === 'add'}
            style={{ padding: '6px 10px' }}
          >
            +
          </button>
          <button
            onClick={() => setEditAction('insert-before')}
            disabled={selectedPointIndex === null}
            title="Insert point before selected point"
            aria-label="Insert point before selected point"
            style={{ padding: '6px 8px' }}
          >
            Ins
          </button>
          <button
            onClick={handleDeleteSelectedPoint}
            disabled={selectedPointIndex === null}
            style={{ backgroundColor: '#dc3545' }}
            title="Delete selected point"
            aria-label="Delete selected point"
          >
            🗑
          </button>
        </div>

        {selectedPoint ? (
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '8px' }}>
            <h4 style={{ margin: '0 0 4px' }}>Selected point {selectedPointIndex + 1}</h4>
            <div style={{ fontSize: '0.85rem', color: 'var(--secondary-text-color)', marginBottom: '8px' }}>
              <div>Previous leg: {previousLegDistanceInMeters === null ? 'Start point' : `${Math.round(previousLegDistanceInMeters)} m`}</div>
              <div>Next leg: {nextLegDistanceInMeters === null ? 'End point' : `${Math.round(nextLegDistanceInMeters)} m`}</div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ width: '28px' }}>Lat</span>
              <input
                type="number"
                step="any"
                value={tempCoords.lat}
                onChange={(event) => setTempCoords((current) => ({ ...current, lat: event.target.value }))}
                onBlur={handleSelectedCoordsSave}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                  if (event.key === 'Escape') {
                    setTempCoords({ lat: selectedPoint.lat.toString(), lng: selectedPoint.lng.toString() });
                    event.currentTarget.blur();
                  }
                }}
                style={{ flex: 1, minWidth: 0, padding: '6px' }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '28px' }}>Lng</span>
              <input
                type="number"
                step="any"
                value={tempCoords.lng}
                onChange={(event) => setTempCoords((current) => ({ ...current, lng: event.target.value }))}
                onBlur={handleSelectedCoordsSave}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                  if (event.key === 'Escape') {
                    setTempCoords({ lat: selectedPoint.lat.toString(), lng: selectedPoint.lng.toString() });
                    event.currentTarget.blur();
                  }
                }}
                style={{ flex: 1, minWidth: 0, padding: '6px' }}
              />
            </label>
            <p style={{ fontSize: '0.8rem', color: 'var(--secondary-text-color)' }}>The 100 m matching threshold is shown around this point.</p>
          </div>
        ) : (
          <p style={{ fontSize: '0.85rem', color: 'var(--secondary-text-color)' }}>Select a point on the map to edit its coordinates.</p>
        )}

        <div style={{ marginTop: '20px', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
          {seriesId && swimId ? (
            <>
              <button
                onClick={handleLoadPrelimMatches}
              disabled={loadingPrelimMatches || editAction !== null}
                style={{ width: '100%', padding: '8px', backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: loadingPrelimMatches ? 'default' : 'pointer', opacity: loadingPrelimMatches ? 0.7 : 1, marginBottom: '8px' }}
              >
                {loadingPrelimMatches ? '⏳ Loading matches...' : '🔍 Load Preliminary Matches'}
              </button>
              {prelimMatches.length > 0 && (
                <div style={{ fontSize: '0.78rem', color: '#555', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <strong>{prelimMatches.length} overlaid (read-only):</strong>
                    <button onClick={() => setprelimMatches([])} style={{ padding: '1px 6px', fontSize: '0.72rem', backgroundColor: '#888', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>✕ Clear</button>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ display: 'inline-block', width: '16px', height: '4px', backgroundColor: '#22c55e', borderRadius: '2px' }}></span> Match
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ display: 'inline-block', width: '16px', height: '4px', backgroundColor: '#f97316', borderRadius: '2px' }}></span> No match
                    </span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p style={{ fontSize: '0.78rem', color: '#aaa', marginBottom: '8px' }}>Save the swim first to load match overlays.</p>
          )}
        </div>

        <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
          <button onClick={handleSave} style={{ flex: 1, padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Route</button>
          <button onClick={onCancel} style={{ flex: 1, padding: '10px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>

      {/* Map Area */}
      <div style={{ flex: 1, position: 'relative' }}>
        <Map
          ref={mapRef}
          initialViewState={initialViewState}
          style={{ width: '100%', height: '100%' }}
          mapStyle={mapStyle}
          mapboxAccessToken={mapboxToken}
          onClick={handleMapClick}
          cursor={editAction ? 'crosshair' : 'grab'}
          minZoom={1}
          maxZoom={18}
        >
          <ScaleControl />
          <Source id="route-source" type="geojson" data={routeGeoJSON}>
            <Layer {...lineLayerStyle} />
          </Source>
          <Source id="matching-threshold" type="geojson" data={thresholdGeoJSON}>
            <Layer id="matching-threshold-fill" type="fill" paint={{ 'fill-color': '#007bff', 'fill-opacity': 0.18 }} />
            <Layer id="matching-threshold-outline" type="line" paint={{ 'line-color': '#007bff', 'line-width': 2, 'line-opacity': 0.7 }} />
          </Source>

          {points.map((p, index) => (
            <Marker
              key={index}
              longitude={p.lng}
              latitude={p.lat}
              draggable
              onDragStart={() => {
                setSelectedPointIndex(index);
                setEditAction(null);
              }}
              onDragEnd={(event) => movePointByDrag(index, event)}
            >
              <button
                className={`known-swim-point ${selectedPointIndex === index ? 'selected' : ''}`}
                aria-label={`Select route point ${index + 1}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedPointIndex(index);
                  setEditAction(null);
                }}
              />
            </Marker>
          ))}
          {/* Preliminary match overlays — read-only, no interaction */}
          {prelimMatches.map((m) => {
            if (!m.coords || m.coords.length < 2) return null;
            return (
              <Source key={`editor-prelim-${m.id}`} id={`editor-prelim-${m.id}`} type="geojson" data={{ type: 'Feature', geometry: { type: 'LineString', coordinates: m.coords } }}>
                <Layer
                  id={`editor-prelim-line-${m.id}`}
                  type="line"
                  layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                  paint={{ 'line-color': m.routeMatch ? '#22c55e' : '#f97316', 'line-width': 2.5, 'line-opacity': 0.8 }}
                />
              </Source>
            );
          })}
        </Map>
        <button
          onClick={() => setMapStyle(prev => prev === 'mapbox://styles/mapbox/streets-v11' ? 'mapbox://styles/mapbox/satellite-streets-v12' : 'mapbox://styles/mapbox/streets-v11')}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 1,
            padding: '8px 12px',
            backgroundColor: 'var(--surface-color)',
            color: 'var(--text-color)',
            border: 'none',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          {mapStyle === 'mapbox://styles/mapbox/streets-v11' ? '🛰️ Satellite' : '🗺️ Streets'}
        </button>
      </div>
    </div>
  );
};

export default SwimRouteEditor;